import {ChevronLeft, ChevronRight} from 'lucide-react';

/**
 * Default rows-per-page choices (cbs-portal demo Pagination, plus 20 — the
 * default page size of many tables, which must be selectable or the select
 * renders the wrong value).
 */
export const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 50] as const;

export type ListPaginationFooterProps = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** When provided, renders the rows-per-page selector. */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: readonly number[];
  /** Shown after the total in the range line, e.g. "members" → "… of 12 members". */
  rangeEntityLabel?: string;
  /** @deprecated Range numbers are always emphasized now (demo parity). */
  emphasizeRange?: boolean;
  disabled?: boolean;
};

/**
 * Portal-wide table pagination footer (cbs-portal demo shape): "Showing X–Y of Z",
 * an optional rows-per-page selector, and prev/next chevrons around "Page X of Y".
 */
export function ListPaginationFooter({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  rangeEntityLabel,
  disabled = false,
}: ListPaginationFooterProps) {
  if (total <= 0) {
    return null;
  }

  const safeTotalPages = Math.max(1, totalPages);
  const current = Math.min(page, safeTotalPages);
  const start = (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);
  const suffix = rangeEntityLabel ? ` ${rangeEntityLabel}` : '';

  const navBtn =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-foreground/70 transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:cursor-pointer enabled:hover:bg-secondary';

  return (
    <div className='flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3'>
      <p className='text-xs tabular-nums text-muted-foreground'>
        Showing{' '}
        <span className='font-semibold text-foreground/80'>
          {start}–{end}
        </span>{' '}
        of <span className='font-semibold text-foreground/80'>{total}</span>
        {suffix}
      </p>

      <div className='flex items-center gap-4'>
        {onPageSizeChange && (
          <label className='flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground'>
            Rows per page
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={disabled}
              className='cursor-pointer rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground'
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className='flex items-center gap-2'>
          <button
            type='button'
            className={navBtn}
            onClick={() => onPageChange(current - 1)}
            disabled={current <= 1 || disabled}
            aria-label='Previous page'
          >
            <ChevronLeft className='h-4 w-4' />
          </button>
          <span className='px-1 text-xs tabular-nums text-foreground/70'>
            Page <span className='font-semibold'>{current}</span> of {safeTotalPages}
          </span>
          <button
            type='button'
            className={navBtn}
            onClick={() => onPageChange(current + 1)}
            disabled={current >= safeTotalPages || disabled}
            aria-label='Next page'
          >
            <ChevronRight className='h-4 w-4' />
          </button>
        </div>
      </div>
    </div>
  );
}
